import { Mutation } from "@apollo/client/react/components";
import { SearchAlgoliaIndex } from "@govlaunch/algolia";
import { Box, Button, Mobile, Modal } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { StoryProductSelect } from "@govlaunch/select";
import { FORM_ERROR } from "final-form";
import React from "react";
import { Field, Form } from "react-final-form";
import SetGovernmentProductStack from "~/lib/mutations/SetGovernmentProductStack";
import {
  ISetGovernmentProductStackMutation,
  ISetGovernmentProductStackMutationVariables,
} from "~/lib/mutations/__generated__/SetGovernmentProductStack.generated";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import { ICity, IGovernment } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import { Margin } from "~/components/spacings";
import { FormattedMessage, useIntl } from "react-intl";

interface IAddProductsToStackProps {
  government: Pick<IGovernment, "_id" | "name" | "logo" | "isInnovator"> & {
    city: Pick<ICity, "name">;
  };
  productsInStack: NonNullable<NonNullable<IGovernmentQuery["government"]>["products"]>;
  isOpen: boolean;
  toggleOpen: () => any;
}

const AddProductsToStack: React.FunctionComponent<IAddProductsToStackProps> = ({
  government,
  productsInStack,
  isOpen,
  toggleOpen,
}) => {
  const intl = useIntl();
  const user = useSelfie();

  const maxProductSuggestions = intl.formatMessage({
    id: "Hbm59/",
    defaultMessage: "You've reached the limit of 50 product suggestions per day",
    description: "Error message for when a user reaches the limit of product suggestions",
  });

  const couldNotUpdate = intl.formatMessage({
    defaultMessage: "Could not update government stack",
    id: "yK+c09",
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={toggleOpen}
      ariaHideApp={false}
      overlayStyle={{
        zIndex: 1000,
      }}
      style={{
        overflow: "visible",
      }}
    >
      <Box p={15} width={[340, 560, 560, 768]}>
        <Mobile>
          {(isMobile: boolean) => (
            <div
              css={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ProfilePicture
                  size={isMobile ? 32 : 42}
                  image={government.logo}
                  name={government.name}
                  innovator={government.isInnovator}
                />

                <Margin ml={10}>
                  <h3
                    css={{
                      fontSize: isMobile ? 16 : 23,
                      margin: 0,
                      color: palette.darkGray,
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="Edit {cityName} Product Stack"
                      id="FSpeWm"
                      values={{
                        cityName: government.city.name,
                      }}
                    />
                  </h3>
                </Margin>
              </div>

              <span
                onClick={toggleOpen}
                role="button"
                aria-label="Close"
                css={{
                  marginLeft: "auto",
                  alignSelf: isMobile ? "auto" : "flex-start",
                  color: palette.sealBlue,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": {
                    color: palette.mediumGray,
                  },
                }}
              >
                <span
                  css={{
                    fontSize: 22,
                  }}
                >
                  &times;
                </span>
                <Margin
                  ml={2.5}
                  css={{
                    display: "inline-flex",
                  }}
                >
                  {!isMobile && (
                    <span
                      css={{
                        fontSize: 12,
                        textTransform: "uppercase",
                      }}
                    >
                      <FormattedMessage defaultMessage="Close" id="rbrahO" />
                    </span>
                  )}
                </Margin>
              </span>
            </div>
          )}
        </Mobile>

        <Margin mt={20}>
          <Mutation<ISetGovernmentProductStackMutation, ISetGovernmentProductStackMutationVariables>
            mutation={SetGovernmentProductStack}
          >
            {(setGovernmentProductStack) => {
              return (
                <Form
                  onSubmit={async ({ products }: any) => {
                    const outsideProducts = products.filter((product: any) => product.id);
                    const govlaunchProducts = products.filter((product: any) => product._id);

                    try {
                      await setGovernmentProductStack({
                        variables: {
                          products: govlaunchProducts.map((product: any) => product._id),
                          outsideProducts: outsideProducts.map((outsideProduct: any) => {
                            return {
                              name: outsideProduct.name,
                              url: outsideProduct.url,
                            };
                          }),
                        },
                      }).then(toggleOpen);

                      return null;
                    } catch (e: any) {
                      if (/maximum number of product suggestions/i.test(e.message)) {
                        return {
                          [FORM_ERROR]: maxProductSuggestions,
                        };
                      }

                      return {
                        [FORM_ERROR]: couldNotUpdate,
                      };
                    }
                  }}
                  initialValues={{
                    products: productsInStack,
                  }}
                >
                  {({ handleSubmit, submitError }) => (
                    <form onSubmit={handleSubmit}>
                      <Field name="products">
                        {({ input }) => (
                          <SearchAlgoliaIndex
                            index="products"
                            params={{
                              restrictSearchableAttributes: ["name"],
                            }}
                            user={user}
                          >
                            {({ results: products, searchValue: inputValue, onChange: onInputValueChange }: any) => (
                              <StoryProductSelect
                                products={input.value}
                                placeholder={intl.formatMessage({
                                  defaultMessage: "What are the products you want to add?",
                                  id: "cbABH5",
                                })}
                                inputValue={inputValue}
                                onInputValueChange={onInputValueChange}
                                selectableProducts={products}
                                onChange={input.onChange}
                                css={{
                                  minWidth: 300,
                                }}
                              />
                            )}
                          </SearchAlgoliaIndex>
                        )}
                      </Field>

                      <Margin
                        mt={20}
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Button type="submit" flex={0.7} theme="primary" color="success">
                          <FormattedMessage defaultMessage="Save Changes" id="3VI9mt" />
                        </Button>

                        <Margin
                          ml={10}
                          style={{
                            flex: 0.3,
                          }}
                        >
                          <Button type="button" width="100%" theme="secondary" color="success" onClick={toggleOpen}>
                            <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                          </Button>
                        </Margin>
                      </Margin>
                      {submitError && (
                        <Margin mt={8}>
                          <div css={{ color: palette.red, fontSize: 14 }}>{submitError}</div>
                        </Margin>
                      )}
                    </form>
                  )}
                </Form>
              );
            }}
          </Mutation>
        </Margin>
      </Box>
    </Modal>
  );
};

export default AddProductsToStack;
